import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { styled, Theme } from '@mui/material/styles';

interface CarouselProps {
  title: string;
  children: React.ReactNode;
}

const CarouselContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  position: 'relative',
  width: '100%',
  marginBottom: theme.spacing(4),
}));

const ScrollContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  gap: theme.spacing(2),
  padding: theme.spacing(1),
}));

const ScrollButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  zIndex: 1,
}));

const Carousel: React.FC<CarouselProps> = ({ title, children }) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const newScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  return (
    <CarouselContainer>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <ScrollButton 
        onClick={() => scroll('left')}
        sx={{ left: 0 }}
      >
        <ChevronLeft />
      </ScrollButton>
      <ScrollButton 
        onClick={() => scroll('right')}
        sx={{ right: 0 }}
      >
        <ChevronRight />
      </ScrollButton>
      <ScrollContainer ref={scrollContainerRef}>
        {children}
      </ScrollContainer>
    </CarouselContainer>
  );
};

export default Carousel; 